import type { Address } from 'viem'

import { screenAddress } from '@repo/address-screening/client'
import { mins, tryCatch } from '@repo/utils'
import { useQuery } from '@tanstack/react-query'

/**
 * Screens against the consumer's own route (e.g. `/api/screen-address`, backed
 * by Chainalysis + the on-chain sanctions oracle). Delegates to the shared
 * `@repo/address-screening` client, which POSTs `{ address }` and **fails
 * closed** on any error.
 */
async function isAuthorized(address: Address | undefined, endpoint: string): Promise<boolean> {
  if (!address) return true

  // `screenAddress` already fails closed on any error, but guard here too so the
  // hook's fail-closed guarantee is self-contained and never silently flips to
  // fail-open (via the `initialData: true` seed) if that contract regresses.
  const { data, error } = await tryCatch(screenAddress(address, { endpoint }))
  if (error) {
    console.error(error)
    return false
  }
  return data.isAuthorized
}

/**
 * React Query hook resolving whether `address` is authorized (not screened out).
 *
 * Screening always goes through the consumer's own same-origin route and
 * **fails closed** (any error → unauthorized). An undefined address is always
 * authorized.
 *
 * Seeds `initialData: true` (optimistic) so the UI is not gated on the first
 * screen, but `initialDataUpdatedAt: 0` marks that seed stale so the query still
 * screens on mount (without it, `staleTime` would treat the seed as fresh and
 * the address would never be screened).
 *
 * The verdict is stable for a connected address and each POST is a paid,
 * rate-limited Chainalysis + oracle call, so it's held fresh for 5 minutes and
 * window refocus does not refetch. A new address rekeys the query and
 * re-screens; a same-address reconnect within that window reuses the cached
 * verdict (add a `refetchInterval` for active mid-session re-screening).
 *
 * @param {Address | undefined} address - Address to screen; `undefined` always resolves authorized.
 * @param options - Query options.
 * @param {boolean} [options.enabled=true] - Whether the query runs.
 * @param {string} options.screenAddressEndpoint - Consumer route to screen against.
 */
export function useAuthorizedUserQuery(
  address: Address | undefined,
  { enabled = true, screenAddressEndpoint }: { enabled?: boolean; screenAddressEndpoint: string }
) {
  return useQuery({
    queryKey: ['web3.authorized', address, screenAddressEndpoint],
    queryFn: () => isAuthorized(address, screenAddressEndpoint),
    enabled,
    initialData: true,
    // Seed is optimistic but stale-on-mount, so screening still runs immediately.
    initialDataUpdatedAt: 0,
    staleTime: mins(5).toMs(),
    refetchOnWindowFocus: false
  })
}
